/**
 * stats.ts —— HUD 指标（VPIP / PFR / AF），口径严格按 PRD 2.2
 *
 * 数据源是引擎的手牌历史事件流，因此与 AI 决策日志天然同源（AC5.3：误差为 0）。
 *   VPIP = 主动投入筹码入池手数 / 总手数（大盲被动过牌不计）
 *   PFR  = 翻牌前加注手数 / 总手数
 *   AF   =（下注次数 + 加注次数）/ 跟注次数（跟注为 0 时记为上限值 99）
 */

import type { HandRecord } from '../core/game';

export interface SeatStats {
  readonly hands: number;
  readonly vpipHands: number;
  readonly pfrHands: number;
  readonly bets: number;
  readonly raises: number;
  readonly calls: number;
}

export interface SeatStatsView {
  readonly hands: number;
  /** 百分比数值（0–100），样本不足时为 null。 */
  readonly vpip: number | null;
  readonly pfr: number | null;
  readonly af: number | null;
  /** AF 是否为「跟注 0 次」的上限值（HUD 显示 99+）。 */
  readonly afCapped: boolean;
}

/** HUD 样本门槛：不足 20 手显示短横线（设计方案 4.4 席位卡「加载 / 空」态）。 */
export const HUD_MIN_SAMPLE = 20;
const AF_CAP = 99;

export class StatsBook {
  private readonly seats = new Map<number, SeatStats>();
  private ingested = new Set<number>();

  /** 吸收一手已完成的记录（幂等：同一手不会重复计入）。 */
  ingest(rec: HandRecord): void {
    if (this.ingested.has(rec.handNumber)) return;
    this.ingested.add(rec.handNumber);

    const dealt = new Set<number>();
    const vpip = new Set<number>();
    const pfr = new Set<number>();
    const delta = new Map<number, { bets: number; raises: number; calls: number }>();
    const bump = (seat: number, key: 'bets' | 'raises' | 'calls'): void => {
      const d = delta.get(seat) ?? { bets: 0, raises: 0, calls: 0 };
      d[key] += 1;
      delta.set(seat, d);
    };

    for (const ev of rec.events) {
      if (ev.kind === 'deal') dealt.add(ev.seat);
      if (ev.kind !== 'action') continue;
      const { seat, type } = ev.effect;
      // 归一化后的 type：allin 已被翻译为 call / bet / raise，正好符合 AF 口径
      if (type === 'bet') bump(seat, 'bets');
      else if (type === 'raise') bump(seat, 'raises');
      else if (type === 'call') bump(seat, 'calls');
      if (ev.street === 'preflop') {
        if (type === 'call' || type === 'bet' || type === 'raise') vpip.add(seat);
        if (type === 'bet' || type === 'raise') pfr.add(seat);
      }
    }

    for (const seat of dealt) {
      const prev = this.seats.get(seat) ?? { hands: 0, vpipHands: 0, pfrHands: 0, bets: 0, raises: 0, calls: 0 };
      const d = delta.get(seat) ?? { bets: 0, raises: 0, calls: 0 };
      this.seats.set(seat, {
        hands: prev.hands + 1,
        vpipHands: prev.vpipHands + (vpip.has(seat) ? 1 : 0),
        pfrHands: prev.pfrHands + (pfr.has(seat) ? 1 : 0),
        bets: prev.bets + d.bets,
        raises: prev.raises + d.raises,
        calls: prev.calls + d.calls,
      });
    }
  }

  ingestAll(records: readonly HandRecord[]): void {
    for (const r of records) this.ingest(r);
  }

  raw(seat: number): SeatStats {
    return this.seats.get(seat) ?? { hands: 0, vpipHands: 0, pfrHands: 0, bets: 0, raises: 0, calls: 0 };
  }

  view(seat: number): SeatStatsView {
    const s = this.raw(seat);
    if (s.hands < HUD_MIN_SAMPLE) {
      return { hands: s.hands, vpip: null, pfr: null, af: null, afCapped: false };
    }
    const aggression = s.bets + s.raises;
    const afCapped = s.calls === 0;
    return {
      hands: s.hands,
      vpip: (s.vpipHands / s.hands) * 100,
      pfr: (s.pfrHands / s.hands) * 100,
      af: afCapped ? AF_CAP : aggression / s.calls,
      afCapped,
    };
  }
}
